import {
  Component,
  HostBinding,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  SkipSelf,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import * as Rx from 'rxjs';
import { filter } from 'rxjs/operators';
import { ModalLoadingStatus, ModalService } from './modal.service';

export enum ModalSize {
  XSMALL = 'xsmall',
  SMALL = 'small',
  MID = 'mid',
  LARGE = 'large',
  XLARGE = 'xlarge',
}

export enum ModalColor {
  LIGHT = 'light',
  DARK = 'dark',
}

@Component({
  selector: 'fl-modal',
  template: `
    <fl-bit class="ModalContainer">
      <fl-bit class="ModalDialog" [attr.data-size]="size">
        <fl-bit
          class="ModalContent"
          [attr.data-edge-to-edge]="edgeToEdge"
          [attr.data-color]="color"
        >
          <fl-icon
            class="ModalCloseButton"
            *ngIf="closeable"
            [color]="
              color === ModalColor.LIGHT ? IconColor.DARK : IconColor.LIGHT
            "
            [name]="'ui-close'"
            [size]="IconSize.SMALL"
            (click)="handleEscape()"
          ></fl-icon>
          <ng-container #modalContent></ng-container>

          <ng-container *ngIf="(statusStream$ | async)?.ready === false">
            <fl-spinner [overlay]="true"></fl-spinner>
          </ng-container>

          <ng-container
            *ngIf="(statusStream$ | async)?.errorMessage; let error"
          >
            <fl-modal-error
              [edgeToEdge]="edgeToEdge"
              [errorMessage]="error"
              (retry)="retryOpen()"
            ></fl-modal-error>
          </ng-container>
        </fl-bit>
      </fl-bit>
      <fl-bit
        class="ModalBackdrop"
        [attr.data-backdrop]="backdropActive"
        (click)="handleClick()"
      ></fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnInit, OnDestroy {
  readonly sizeDefault = ModalSize.LARGE;
  readonly closeableDefault = true;
  readonly edgeToEdgeDefault = false;
  readonly colorDefault = ModalColor.LIGHT;

  IconColor = IconColor;
  IconSize = IconSize;
  ModalSize = ModalSize;
  ModalColor = ModalColor;

  @ViewChild('modalContent', { read: ViewContainerRef })
  modalContent: ViewContainerRef;
  @HostBinding('class.IsActive') backdropActive = false;

  // To be set by modal service at `open`
  color = this.colorDefault;
  size = this.sizeDefault;
  closeable = this.closeableDefault;
  edgeToEdge = this.edgeToEdgeDefault;

  openStreamSubscription: Rx.Subscription;
  routeChangeSubscription: Rx.Subscription;
  statusStream$: Rx.Observable<ModalLoadingStatus>;

  constructor(
    private modalService: ModalService,
    @SkipSelf() private injector: Injector,
    private router: Router,
  ) {}

  ngOnInit() {
    this.modalService.setContainer(this.modalContent, this.injector);
    this.openStreamSubscription = this.modalService.openStream$.subscribe(
      open => {
        this.backdropActive = !!open;
        // new modal being opened: read config from modalService
        if (open) {
          const {
            color = this.colorDefault,
            size = this.sizeDefault,
            closeable = this.closeableDefault,
            edgeToEdge = this.edgeToEdgeDefault,
          } = this.modalService.getCurrentConfig();
          this.color = color;
          this.size = size;
          this.closeable = closeable;
          this.edgeToEdge = edgeToEdge;
        }
      },
    );
    this.statusStream$ = this.modalService.statusStream$;

    // automatically close modal on route change
    this.routeChangeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.modalService.close();
      });
  }

  ngOnDestroy() {
    this.openStreamSubscription.unsubscribe();
    this.routeChangeSubscription.unsubscribe();
  }

  handleClick() {
    if (this.closeable) {
      this.modalService.close();
    }
  }

  retryOpen() {
    this.modalService.retryOpen();
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(e?: KeyboardEvent) {
    if (this.closeable) {
      this.modalService.close();
    }
  }
}
